import { captureSentryMessage } from '@apexx/common-composables';
import { onBeforeUnmount, ref, watch, type Ref } from 'vue';

const SLOW_MS = 5_000;
const REFRESH_MS = 10_000;

/**
 * Progressive copy + Sentry breadcrumbs while a hub sub-app launch is pending.
 * "Refresh App" reloads the WebView (no native process restart available).
 */
export function useSlowLaunchFeedback(
	isLaunching: Ref<boolean>,
	appId: Ref<string>,
): {
	showSlowMessage: Ref<boolean>;
	showRefreshLink: Ref<boolean>;
	refreshApp: () => void;
} {
	const showSlowMessage = ref(false);
	const showRefreshLink = ref(false);

	let slowTimer: ReturnType<typeof setTimeout> | undefined;
	let refreshTimer: ReturnType<typeof setTimeout> | undefined;
	let reportedSlow = false;
	let reportedRefreshOffer = false;

	function clearTimers(): void {
		if (slowTimer !== undefined) {
			clearTimeout(slowTimer);
			slowTimer = undefined;
		}
		if (refreshTimer !== undefined) {
			clearTimeout(refreshTimer);
			refreshTimer = undefined;
		}
	}

	function resetUi(): void {
		clearTimers();
		showSlowMessage.value = false;
		showRefreshLink.value = false;
		reportedSlow = false;
		reportedRefreshOffer = false;
	}

	function startTimers(): void {
		resetUi();

		slowTimer = setTimeout(() => {
			showSlowMessage.value = true;
			if (reportedSlow) {
				return;
			}
			reportedSlow = true;
			captureSentryMessage('Hub app launch taking longer than usual', {
				level: 'warning',
				tags: {
					hub_launch: 'slow',
					app_id: appId.value,
				},
				extra: {
					appId: appId.value,
					elapsedMs: SLOW_MS,
				},
			});
		}, SLOW_MS);

		refreshTimer = setTimeout(() => {
			showRefreshLink.value = true;
			if (reportedRefreshOffer) {
				return;
			}
			reportedRefreshOffer = true;
			captureSentryMessage('Hub app launch still pending; refresh offered', {
				level: 'error',
				tags: {
					hub_launch: 'stuck',
					app_id: appId.value,
				},
				extra: {
					appId: appId.value,
					elapsedMs: REFRESH_MS,
				},
			});
		}, REFRESH_MS);
	}

	watch(
		isLaunching,
		(launching) => {
			if (launching) {
				startTimers();
			} else {
				resetUi();
			}
		},
		{ immediate: true },
	);

	onBeforeUnmount(() => {
		resetUi();
	});

	function refreshApp(): void {
		captureSentryMessage('User refreshed app during slow hub launch', {
			level: 'warning',
			tags: {
				hub_launch: 'refresh',
				app_id: appId.value,
			},
			extra: {
				appId: appId.value,
			},
		});
		window.location.reload();
	}

	return { showSlowMessage, showRefreshLink, refreshApp };
}
